/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useRef, useEffect } from 'react';
import { 
  LayoutDashboard, 
  ArrowRightLeft, 
  Wallet, 
  FileText, 
  Settings, 
  Store,
  Plus, 
  TrendingUp, 
  TrendingDown, 
  DollarSign,
  Bell,
  User,
  Menu,
  X,
  Sun,
  Moon,
  Image as ImageIcon,
  Loader2,
  Sparkles,
  LogOut,
  LogIn,
  ShoppingCart,
  Tag,
  Users,
  ChevronDown,
  ChevronRight,
  Receipt,
  FileCheck,
  Truck,
  UserPlus,
  Package,
  BookOpen,
  HelpCircle,
  ShieldCheck,
  MessageSquare,
  Key,
  Eye,
  EyeOff,
  Search
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area
} from 'recharts';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { cn } from './lib/utils';
import {
  APP_LANGUAGE_STORAGE_KEY,
  getStoredLanguage,
  normalizeLanguage,
  notifyLanguageChange,
  translateArabicText,
} from './lib/localization';
import { motion, AnimatePresence } from 'motion/react';

import { analyzeAccountingImage } from './services/geminiService';
import QuotesView from './components/QuotesView';
import InvoicesView from './components/InvoicesView';
import DeliveriesView from './components/DeliveriesView';
import ItemsView from './components/ItemsView';
import InventoryDashboardView from './components/InventoryDashboardView';
import AccountingDashboardView from './components/AccountingDashboardView';
import ChartOfAccountsView from './components/ChartOfAccountsView';
import BankAccountsView from './components/BankAccountsView';
import POSView from './components/POSView';
import POSSalesHistoryView from './components/POSSalesHistoryView';
import POSCatalogView from './components/POSCatalogView';
import SettingsView from './components/SettingsView';
import StoresView from './components/StoresView';
import ProfileView from './components/ProfileView';
import ExpenseSubmissionsView from './components/ExpenseSubmissionsView';
import PurchaseOrdersView from './components/PurchaseOrdersView';
import BillsView from './components/BillsView';
import PurchaseDeliveriesView from './components/PurchaseDeliveriesView';
import LandedCostsView from './components/LandedCostsView';
import ContactsView from './components/ContactsView';
import ReportsView from './components/ReportsView';
import PayRunView from './components/PayRunView';
import EmployeesView from './components/EmployeesView';
import PayrollSettingsView from './components/PayrollSettingsView';
import FixedAssetsView from './components/FixedAssetsView';
import ManualJournalsView from './components/ManualJournalsView';
import CustomerBalancesView from './components/CustomerBalancesView';
import VendorBalancesView from './components/VendorBalancesView';
import TrialBalanceView from './components/TrialBalanceView';
import DiscussionsView from './components/DiscussionsView';
import ActivityLogView from './components/ActivityLogView';
import { SecurityMonitor } from './components/SecurityMonitor';
import AccountingConfigurationView from './components/AccountingConfigurationView';
import AttendanceView from './components/AttendanceView';
import ExecutiveDashboard from './components/ExecutiveDashboard';
import TasksMOPSView from './components/TasksMOPSView';
import HelpView from './components/HelpView';
import SuperAdminView from './components/SuperAdminView';
import AIChatBot from './components/AIChatBot';
import PublicQuotePortal from './components/PublicQuotePortal';
import PublicInvoicePortal from './components/PublicInvoicePortal';
import PublicDocumentPortal from './components/PublicDocumentPortal';
import PublicCatalogView from './components/PublicCatalogView';
import DigitalMenuProductPage from './components/DigitalMenuProductPage';
import TaxBaseView from './components/TaxBaseView';
import TagsView from './components/TagsView';
import LicenseGate from './components/LicenseGate';
import FinancialNotificationsChecker from './components/FinancialNotificationsChecker';
import AppNavigation from './components/AppNavigation';
import { Toaster } from 'sonner';
import { notify } from './services/notify';
import { MODULES } from './constants';
import {
  isBrowserNotificationSupported,
  requestBrowserNotificationPermission,
  showBrowserNotification,
} from './services/browserNotificationService';
import {
  db,
  onSnapshot,
  collection,
  query,
  where,
  orderBy,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  limit,
  getDocs,
  handleFirestoreError,
  OperationType
} from './lib/api';

// Utility for tailwind classes
// Moved to src/lib/utils.ts

// Error Info Interface for Firestore
// Using imported handleFirestoreError and OperationType from ./firebase

// Mock data for initial view
const THEME_VARIABLE_KEYS = [
  '--background', '--foreground', '--primary', '--primary-foreground', '--card', '--card-foreground',
  '--muted', '--muted-foreground', '--secondary', '--secondary-foreground', '--border', '--input',
  '--ring', '--destructive', '--destructive-foreground', '--accountix-navy', '--accountix-navy-hover',
  '--accountix-blue', '--accountix-blue-hover', '--accountix-blue-light', '--gain', '--gain-muted', '--loss',
  '--loss-muted', '--warning', '--warning-muted', '--info', '--info-muted', '--accountix-card-shadow',
] as const;

const MOCK_DATA = [
  { name: 'Jan', income: 4000, expenses: 2400 },
  { name: 'Feb', income: 3000, expenses: 1398 },
  { name: 'Mar', income: 2000, expenses: 9800 },
  { name: 'Apr', income: 2780, expenses: 3908 },
  { name: 'May', income: 1890, expenses: 4800 },
  { name: 'Jun', income: 2390, expenses: 3800 },
  { name: 'Jul', income: 3490, expenses: 4300 },
  { name: 'Aug', income: 4000, expenses: 2400 },
  { name: 'Sep', income: 3000, expenses: 1398 },
  { name: 'Oct', income: 2000, expenses: 9800 },
  { name: 'Nov', income: 2780, expenses: 3908 },
  { name: 'Dec', income: 1890, expenses: 4800 },
];

// handleFirestoreError is imported from ./firebase

interface Organization {
  id: string;
  name: string;
  country?: string;
  industry?: string;
  targetAudience?: string;
  status?: string;
  assignedModules?: string[];
  keyPersonId?: string;
  licenseKey?: string;
  location?: {
    lat: number;
    lng: number;
    address: string;
  };
  subscription?: {
    productId?: string;
    packageId: string;
    purchaseDate: string;
    warningDate: string;
    renewalDate: string;
  };
  createdAt: any;
}

const SUPER_ADMIN_EMAILS = ['abdallahmohamed30991@gmail.com', 'mohdelsakka@gmail.com', 'omar.hitman2010@gmail.com'];
const LOCAL_DEV_USER_EMAIL_KEY = 'local_dev_user_email';
const LOCAL_API_TOKEN_KEY = 'app_auth_token';
const LOCAL_DEV_ORG_ID = '00000000-0000-0000-0000-000000000001';
const isSuperAdminEmail = (email?: string | null) => !!email && SUPER_ADMIN_EMAILS.includes(email.toLowerCase());
const isLocalhost = () => ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
const isLocalDevUser = (user?: any) => !!user && (
  !!localStorage.getItem(LOCAL_API_TOKEN_KEY) ||
  (typeof user?.uid === 'string' && (
    user.uid.startsWith('local-dev-') ||
    user.uid.startsWith('local-admin-')
  ))
);

export default function App() {
  const [user, setUser] = useState<any>(null);
  const [licenseKey, setLicenseKeyState] = useState<string | null>(localStorage.getItem('app_license_key'));

  const setLicenseKey = (key: string | null) => {
    if (key) {
      localStorage.setItem('app_license_key', key);
    } else {
      localStorage.removeItem('app_license_key');
      localStorage.removeItem('app_license_permissions');
    }
    setLicenseKeyState(key);
  };
  const [profile, setProfile] = useState<any>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>(localStorage.getItem('selected_org_id') || '');
  const [selectedStoreId, setSelectedStoreId] = useState<string>(localStorage.getItem('selected_pos_store_id') || '');

  useEffect(() => {
    if (selectedStoreId) {
      localStorage.setItem('selected_pos_store_id', selectedStoreId);
    } else {
      localStorage.removeItem('selected_pos_store_id');
    }
  }, [selectedStoreId]);

  const [currentOrgMembership, setCurrentOrgMembership] = useState<any | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const match = window.location.hash.match(/^#\/app\/([^/]+)(?:\/([^/]+))?$/);
    return match?.[1] ? decodeURIComponent(match[1]) : 'dashboard';
  });
  const [language, setLanguage] = useState<'en' | 'ar'>(() => getStoredLanguage());

  useEffect(() => {
    let tabLabel = '';
    const foundModule = MODULES.find(m => m.id === activeTab);
    if (foundModule) {
      tabLabel = foundModule.label;
    } else {
      for (const m of MODULES) {
        const sub = m.subModules.find(s => s.id === activeTab);
        if (sub) {
          tabLabel = sub.label;
          break;
        }
      }
    }

    if (!tabLabel && activeTab) {
      tabLabel = activeTab.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }

    const localizedLabel = language === 'ar' ? translateArabicText(tabLabel) : tabLabel;
    document.title = localizedLabel ? `${localizedLabel} - Accountix` : 'Accountix';
  }, [activeTab, language]);

  useEffect(() => {
    localStorage.setItem(APP_LANGUAGE_STORAGE_KEY, language);
    notifyLanguageChange();
  }, [language]);
  const [resumedSaleId, setResumedSaleId] = useState<string | null>(null);
  const [isReturn, setIsReturn] = useState(false);
  const [layoutPreference, setLayoutPreference] = useState<'classic' | 'modern' | 'brutalist' | 'pastel-greenery' | 'cherry-blossom' | 'minimal' | 'technical'>('classic');
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({
    sales: false,
    pos: false,
    purchases: false,
    contacts: false,
    items: false,
    accounting: false,
    super_admin: false
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  // On viewports smaller than `lg` (1024px) the sidebar becomes an overlay drawer.
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth < 1024 : false
  );
  // Width the sidebar currently occupies in the layout flow. Zero on mobile,
  // where the sidebar floats above content as a drawer instead of displacing it.
  // Published as a CSS variable so fixed-position overlays can inset themselves
  // by the same amount the main content is offset.
  const sidebarWidth = isMobile ? 0 : isSidebarOpen ? 256 : 72;
  const [isDarkMode, setIsDarkMode] = useState<boolean>(localStorage.getItem('isDarkMode') === 'true');
  const [isOffline, setIsOffline] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(() => {
    const match = window.location.hash.match(/^#\/app\/[^/]+\/([^/]+)$/);
    return match?.[1] ? decodeURIComponent(match[1]) : null;
  });
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState<string | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<string | null>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [bills, setBills] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [showAIChat, setShowAIChat] = useState(false);
  const [aiLauncherPosition, setAiLauncherPosition] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('organization_ai_launcher_position') || 'null');
      return saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)
        ? { x: saved.x, y: saved.y }
        : { x: window.innerWidth - 76, y: window.innerHeight - 76 };
    } catch {
      return { x: window.innerWidth - 76, y: window.innerHeight - 76 };
    }
  });
  const aiLauncherDragRef = useRef<{ pointerId: number; offsetX: number; offsetY: number; moved: boolean } | null>(null);
  const aiLauncherSuppressClickRef = useRef(false);

  // Keep the draggable launcher inside the viewport *and* clear of the sidebar,
  // which is fixed above the content. The sidebar is anchored to the inline
  // start edge, so it occupies the left in LTR and the right in RTL.
  const clampAiLauncherPosition = React.useCallback((x: number, y: number) => {
    const launcherSize = 56;
    const gutter = 8;
    const isRtl = language === 'ar';
    const minX = isRtl ? gutter : sidebarWidth + gutter;
    const maxX = isRtl
      ? Math.max(minX, window.innerWidth - sidebarWidth - launcherSize - gutter)
      : Math.max(minX, window.innerWidth - launcherSize - gutter);
    const maxY = Math.max(gutter, window.innerHeight - launcherSize - gutter);
    return {
      x: Math.min(Math.max(minX, x), maxX),
      y: Math.min(Math.max(gutter, y), maxY),
    };
  }, [language, sidebarWidth]);

  const handleAiLauncherPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    aiLauncherDragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleAiLauncherPointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = aiLauncherDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const next = clampAiLauncherPosition(event.clientX - drag.offsetX, event.clientY - drag.offsetY);
    if (Math.abs(next.x - aiLauncherPosition.x) > 2 || Math.abs(next.y - aiLauncherPosition.y) > 2) {
      drag.moved = true;
    }
    if (drag.moved) setAiLauncherPosition(next);
  };

  const handleAiLauncherPointerUp = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = aiLauncherDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (drag.moved) {
      const finalPosition = clampAiLauncherPosition(event.clientX - drag.offsetX, event.clientY - drag.offsetY);
      setAiLauncherPosition(finalPosition);
      aiLauncherSuppressClickRef.current = true;
      localStorage.setItem('organization_ai_launcher_position', JSON.stringify(finalPosition));
    }
    aiLauncherDragRef.current = null;
  };

  const handleAiLauncherClick = () => {
    if (aiLauncherSuppressClickRef.current) {
      aiLauncherSuppressClickRef.current = false;
      return;
    }
    setShowAIChat(true);
  };

  // Re-clamp when the viewport resizes or the sidebar changes width, so the
  // launcher never ends up parked underneath the sidebar or off-screen.
  useEffect(() => {
    setAiLauncherPosition(current => clampAiLauncherPosition(current.x, current.y));
    const handleResize = () => setAiLauncherPosition(current => clampAiLauncherPosition(current.x, current.y));
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [clampAiLauncherPosition]);

  // Sync selectedOrgId to user document for Firestore Security Rules
  useEffect(() => {
    if (isLocalDevUser(user)) return;
    if (user && selectedOrgId && isAuthReady) {
      const userRef = doc(db, 'users', user.uid);
      
      // Only update if current profile orgId is different to avoid unnecessary writes
      if (profile?.orgId !== selectedOrgId) {
        updateDoc(userRef, {
          orgId: selectedOrgId
        }).catch(err => {
          // Silently fail if it's just a permission issue during initial load
          console.warn('Draft: Failed to sync orgId to user profile:', err.message);
        });
      }
    }
  }, [user, selectedOrgId, isAuthReady, profile?.orgId]);

  const [notifications, setNotifications] = useState<any[]>([]);
  const knownNotificationIdsRef = useRef<Set<string> | null>(null);

  const applyLoadedNotifications = React.useCallback((rows: any[]) => {
    const currentIds = new Set(
      rows
        .map(notification => notification?.id)
        .filter(Boolean)
        .map(id => String(id)),
    );
    const previousIds = knownNotificationIdsRef.current;

    if (previousIds) {
      rows
        .filter(notification => {
          const id = notification?.id ? String(notification.id) : '';
          return Boolean(id) && !notification.read && !previousIds.has(id);
        })
        .forEach(notification => {
          showBrowserNotification({
            id: String(notification.id),
            title: notification.title || 'New notification',
            message: notification.message,
          });
        });
    }

    knownNotificationIdsRef.current = currentIds;
    setNotifications(rows);
  }, []);

  useEffect(() => {
    knownNotificationIdsRef.current = null;
    setNotifications([]);
  }, [user, isAuthReady, selectedOrgId]);

  const [stats, setStats] = useState({ 
    balance: 0, 
    income: 0, 
    expenses: 0,
    thisMonthRevenue: 0,
    lastMonthRevenue: 0,
    thisMonthExpenses: 0,
    lastMonthExpenses: 0,
    billsAwaitingPayment: 0,
    invoicesAwaitingPayment: 0
  });
  const [orgSettings, setOrgSettings] = useState<any>(null);
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [licenseInput, setLicenseInput] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [portalQuoteId, setPortalQuoteId] = useState<string | null>(null);
  const [portalInvoiceId, setPortalInvoiceId] = useState<string | null>(null);
  const [portalBillId, setPortalBillId] = useState<string | null>(null);
  const [portalPurchaseOrderId, setPortalPurchaseOrderId] = useState<string | null>(null);
  const [menuProductId, setMenuProductId] = useState<string | null>(null);
  const [publicCatalogOrgId, setPublicCatalogOrgId] = useState<string | null>(null);

  // Hash router for public portals and authenticated app deep links.
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      const appMatch = hash.match(/^#\/app\/([^/]+)(?:\/([^/]+))?$/);
      const quoteMatch = hash.match(/#\/portal\/quote\/(.+)/);
      const invoiceMatch = hash.match(/#\/portal\/invoice\/(.+)/);
      const billMatch = hash.match(/#\/portal\/bill\/(.+)/);
      const purchaseOrderMatch = hash.match(/#\/portal\/purchase-order\/(.+)/);
      const menuMatch = hash.match(/#\/menu\/product\/(.+)/);
      const catalogMatch = hash.match(/#\/catalog\/(.+)/);
      
      if (appMatch) {
        setActiveTab(decodeURIComponent(appMatch[1]));
        setSelectedEntityId(appMatch[2] ? decodeURIComponent(appMatch[2]) : null);
        setPortalQuoteId(null);
        setPortalInvoiceId(null);
        setPortalBillId(null);
        setPortalPurchaseOrderId(null);
        setMenuProductId(null);
        setPublicCatalogOrgId(null);
      } else if (quoteMatch) {
        setPortalQuoteId(decodeURIComponent(quoteMatch[1]));
        setPortalInvoiceId(null);
        setPortalBillId(null);
        setPortalPurchaseOrderId(null);
        setMenuProductId(null);
        setPublicCatalogOrgId(null);
      } else if (invoiceMatch) {
        setPortalInvoiceId(decodeURIComponent(invoiceMatch[1]));
        setPortalQuoteId(null);
        setPortalBillId(null);
        setPortalPurchaseOrderId(null);
        setMenuProductId(null);
        setPublicCatalogOrgId(null);
      } else if (billMatch) {
        setPortalBillId(decodeURIComponent(billMatch[1]));
        setPortalQuoteId(null);
        setPortalInvoiceId(null);
        setPortalPurchaseOrderId(null);
        setMenuProductId(null);
        setPublicCatalogOrgId(null);
      } else if (purchaseOrderMatch) {
        setPortalPurchaseOrderId(decodeURIComponent(purchaseOrderMatch[1]));
        setPortalQuoteId(null);
        setPortalInvoiceId(null);
        setPortalBillId(null);
        setMenuProductId(null);
        setPublicCatalogOrgId(null);
      } else if (menuMatch) {
        setMenuProductId(menuMatch[1]);
        setPortalQuoteId(null);
        setPortalInvoiceId(null);
        setPortalBillId(null);
        setPortalPurchaseOrderId(null);
        setPublicCatalogOrgId(null);
      } else if (catalogMatch) {
        setPublicCatalogOrgId(catalogMatch[1]);
        setPortalQuoteId(null);
        setPortalInvoiceId(null);
        setPortalBillId(null);
        setPortalPurchaseOrderId(null);
        setMenuProductId(null);
      } else {
        setPortalQuoteId(null);
        setPortalInvoiceId(null);
        setPortalBillId(null);
        setPortalPurchaseOrderId(null);
        setMenuProductId(null);
        setPublicCatalogOrgId(null);
      }
    };

    handleHashChange(); // Initial check
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const updateAppHash = React.useCallback((tab: string, id?: string | null) => {
    const nextHash = `#/app/${encodeURIComponent(tab)}${id ? `/${encodeURIComponent(id)}` : ''}`;
    if (window.location.hash !== nextHash) window.location.hash = nextHash;
  }, []);

  // Keep ordinary tab changes shareable too, including navigation callbacks that
  // still set activeTab directly in older views.
  useEffect(() => {
    // Do not overwrite a public hash route while its target state is being
    // initialized. On a cold QR scan, the route parser and this effect run in
    // the same render; checking the URL prevents the portal route being
    // replaced by /app/dashboard before the portal can render.
    const hash = window.location.hash;
    if (/^#\/(?:portal\/(?:quote|invoice|bill|purchase-order)|menu\/product|catalog)\//.test(hash)) return;
    if (portalQuoteId || portalInvoiceId || menuProductId || publicCatalogOrgId) return;
    const current = hash.match(/^#\/app\/([^/]+)(?:\/([^/]+))?$/);
    const currentTab = current?.[1] ? decodeURIComponent(current[1]) : null;
    const idForTab = currentTab === activeTab ? selectedEntityId : null;
    if (currentTab !== activeTab && selectedEntityId) setSelectedEntityId(null);
    updateAppHash(activeTab, idForTab);
  }, [activeTab, selectedEntityId, portalQuoteId, portalInvoiceId, menuProductId, publicCatalogOrgId, updateAppHash]);


  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailInput.trim()) {
      notify.error('Please enter your username or email address');
      return;
    }
    if (!passwordInput) {
      notify.error('Please enter your password');
      return;
    }

    setIsLoggingIn(true);
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailInput.trim(),
          username: emailInput.trim(),
          identifier: emailInput.trim(),
          password: passwordInput,
        }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || 'Failed to log in');

      localStorage.setItem(LOCAL_API_TOKEN_KEY, payload.token);
      localStorage.removeItem(LOCAL_DEV_USER_EMAIL_KEY);
      if (payload.user?.orgId) localStorage.setItem('selected_org_id', payload.user.orgId);
      notify.confirm('Logged in successfully');
      window.location.reload();
    } catch (err: any) {
      console.error('Login error:', err);
      notify.error(err.message || 'Failed to login');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLicenseLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!licenseInput.trim()) return;

    setIsActivating(true);
    try {
      const key = licenseInput.trim();
      const deviceId = localStorage.getItem('device_fingerprint') || `dev_${crypto.randomUUID()}`;
      const response = await fetch('/api/licenses/activate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          licenseKey: key,
          deviceId,
          deviceName: navigator.userAgent.substring(0, 50),
          platform: navigator.platform || 'Web'
        })
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) throw new Error(payload?.error || 'License activation failed');

      localStorage.setItem('device_fingerprint', deviceId);
      setLicenseKey(key);
      localStorage.setItem('app_license_key', key);
      const permissions = payload?.license?.permissions || [];
      localStorage.setItem('app_license_permissions', JSON.stringify(permissions));
      
      if (payload?.orgId) {
        setSelectedOrgId(payload.orgId);
        localStorage.setItem('selected_org_id', payload.orgId);
      }
      
      notify.confirm('License activated successfully!');
    } catch (err: any) {
      console.error(err);
      notify.error(err.message || 'Connection to license server failed');
    } finally {
      setIsActivating(false);
    }
  };

  useEffect(() => {
    if (selectedOrgId) {
      localStorage.setItem('selected_org_id', selectedOrgId);
    }
  }, [selectedOrgId]);

  useEffect(() => {
    if (isLocalDevUser(user)) {
      const token = localStorage.getItem(LOCAL_API_TOKEN_KEY);
      if (!token || !selectedOrgId) {
        setOrgSettings(null);
        return;
      }
      fetch(`/api/organizations/${selectedOrgId}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(async response => response.ok ? response.json() : null)
        .then(settings => setOrgSettings(settings ? {
          ...settings,
          defaultCurrency: settings.defaultCurrency ?? settings.default_currency ?? settings.currency ?? 'USD',
          defaultAccounts: settings.defaultAccounts ?? settings.default_accounts,
        } : null))
        .catch(error => {
          console.warn('Local settings load failed:', error);
          setOrgSettings(null);
        });
      return;
    }
    if (selectedOrgId && user) {
      const unsub = onSnapshot(doc(db, 'settings', selectedOrgId), (doc) => {
        if (doc.exists()) {
          const settings = doc.data();
          setOrgSettings({
            ...settings,
            defaultCurrency: settings.defaultCurrency ?? settings.default_currency ?? settings.currency ?? 'USD',
            defaultAccounts: settings.defaultAccounts ?? settings.default_accounts,
          });
        }
      }, (error) => {
        handleFirestoreError(error, OperationType.GET, `settings/${selectedOrgId}`);
      });
      return () => unsub();
    }
  }, [selectedOrgId, user]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Connection Monitor
  useEffect(() => {
    const checkConnection = async () => {
      if (!window.navigator.onLine) {
        setIsOffline(true);
        return;
      }

      try {
        const response = await fetch('/api/health/db');
        setIsOffline(!response.ok);
      } catch (error) {
        if (error instanceof Error && (error.message.includes('offline') || error.message.includes('unavailable') || error.message.includes('permission-denied'))) {
          // If it's permission denied, the service is reachable but we might not have a test doc
          // We'll treat it as online but maybe log a warning
          if (error.message.includes('permission-denied')) {
            setIsOffline(false);
          } else {
            setIsOffline(true);
          }
        }
      }
    };

    const interval = setInterval(checkConnection, 60000); // Check every minute instead of 30sec
    checkConnection(); // Check immediately on mount
    
    window.addEventListener('online', checkConnection);
    window.addEventListener('offline', () => setIsOffline(true));
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('online', checkConnection);
      window.removeEventListener('offline', () => setIsOffline(true));
    };
  }, []);

  // Auth: load current user from the PostgreSQL JWT session if present.
  // Replaces the previous Firebase onAuthStateChanged listener.
  useEffect(() => {
    let cancelled = false;

    const loadSession = async () => {
      const token = localStorage.getItem(LOCAL_API_TOKEN_KEY);
      const localDevEmail = localStorage.getItem(LOCAL_DEV_USER_EMAIL_KEY);

      if (token) {
        try {
          const response = await fetch('/api/auth/me', {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!response.ok) {
            const payload = await response.json().catch(() => null);
            const error = new Error(payload?.error || 'Session is no longer valid');
            (error as Error & { code?: string }).code = payload?.code;
            throw error;
          }
          const localUser = await response.json();
          if (cancelled) return;
          const sessionLanguage = normalizeLanguage(localUser.language || getStoredLanguage());

          // Load this user's organizations before exposing the authenticated user
          // to organization-scoped effects. The persisted organization may differ
          // from the organization embedded in the current JWT after a refresh.
          const orgResponse = await fetch('/api/organizations', {
            headers: { Authorization: `Bearer ${token}` },
          });
          const orgRows = orgResponse.ok ? await orgResponse.json() : [];
          if (cancelled) return;
          const organizationsFromApi: Organization[] = (orgRows || []).map((org: any) => ({
            id: org.id,
            name: org.name,
            country: org.country,
            industry: org.industry,
            targetAudience: org.target_audience,
            status: org.status,
            assignedModules: org.assigned_modules || [],
            keyPersonId: org.key_person_id,
            licenseKey: org.license_key,
            location: org.location || {},
            subscription: org.subscription || {},
            createdAt: org.created_at,
          }));
          const availableOrganizationIds = new Set(organizationsFromApi.map(organization => organization.id));
          const savedOrganizationId = localStorage.getItem('selected_org_id');
          const next = [savedOrganizationId, localUser.orgId, organizationsFromApi[0]?.id]
            .find(organizationId => Boolean(organizationId && availableOrganizationIds.has(organizationId))) || '';

          if (next) {
            const selectionResponse = await fetch('/api/auth/select-organization', {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ orgId: next }),
            });
            const selectionPayload = await selectionResponse.json().catch(() => null);
            if (!selectionResponse.ok || !selectionPayload?.token) {
              throw new Error(selectionPayload?.error || 'Could not restore the selected organization');
            }
            localStorage.setItem(LOCAL_API_TOKEN_KEY, selectionPayload.token);
            localStorage.setItem('selected_org_id', selectionPayload.orgId || next);
          } else {
            localStorage.removeItem('selected_org_id');
          }

          if (cancelled) return;
          localStorage.setItem(APP_LANGUAGE_STORAGE_KEY, sessionLanguage);
          setUser({ ...localUser, mustChangePassword: Boolean(localUser.mustChangePassword) });
          setProfile({
            uid: localUser.uid,
            displayName: localUser.displayName || localUser.email,
            email: localUser.email,
            photoURL: localUser.photoURL || '',
            role: localUser.role,
            language: sessionLanguage,
            layoutPreference: localUser.layoutPreference || 'classic',
            mustChangePassword: Boolean(localUser.mustChangePassword),
          });
          setLanguage(sessionLanguage);
          setLayoutPreference(localUser.layoutPreference || 'classic');
          setOrganizations(organizationsFromApi);
          setSelectedOrgId(next);
        } catch (error) {
          if (cancelled) return;
          console.warn('Clearing invalid database session:', error);
          const sessionError = error as Error & { code?: string };
          if (sessionError.code === 'auth/user-suspended' || sessionError.message === 'This user is suspended') {
            notify.error('This user is suspended');
          }
          localStorage.removeItem(LOCAL_API_TOKEN_KEY);
          localStorage.removeItem(LOCAL_DEV_USER_EMAIL_KEY);
          setUser(null);
          setProfile(null);
          setSelectedOrgId('');
        }
      } else if (isLocalhost() && isSuperAdminEmail(localDevEmail)) {
        // Legacy Firebase-disabled bypass kept for local dev convenience.
        if (cancelled) return;
        const localDevUser = {
          uid: `local-dev-${localDevEmail}`,
          email: localDevEmail,
          displayName: 'Local Dev Admin',
          photoURL: '',
        };
        setUser(localDevUser);
        setProfile({ uid: localDevUser.uid, displayName: localDevUser.displayName, email: localDevEmail, photoURL: '', role: 'admin', language });
        setOrganizations([{ id: LOCAL_DEV_ORG_ID, name: 'Local Development Organization', status: 'Active', assignedModules: [], createdAt: new Date().toISOString() }]);
        setSelectedOrgId(LOCAL_DEV_ORG_ID);
        localStorage.setItem('selected_org_id', LOCAL_DEV_ORG_ID);
      } else {
        if (cancelled) return;
        setUser(null);
        setProfile(null);
        setSelectedOrgId('');
      }
      if (!cancelled) setIsAuthReady(true);
    };

    loadSession();
    return () => { cancelled = true; };
  }, []);

  // Update current membership and user doc when selectedOrgId changes
  useEffect(() => {
    if (!user || !selectedOrgId) {
      setCurrentOrgMembership(null);
      return;
    }
    if (isLocalDevUser(user)) {
      setCurrentOrgMembership({ role: 'admin', permissions: [] });
      return;
    }

    // Local PostgreSQL session: membership is implicit; admin role comes from the JWT.
    setCurrentOrgMembership({ role: user.role || 'admin', permissions: [] });
  }, [user, selectedOrgId]);

  // Real-time Transactions, Invoices, Bills, and Users Listener
  useEffect(() => {
    if (!user || !isAuthReady || !selectedOrgId) {
      knownNotificationIdsRef.current = null;
      setNotifications([]);
      return;
    }

    if (isLocalDevUser(user)) {
      setTransactions([]);
      setInvoices([]);
      setBills([]);
      setUsers([]);

      const token = localStorage.getItem(LOCAL_API_TOKEN_KEY);
      if (!token) {
        knownNotificationIdsRef.current = null;
        setNotifications([]);
        return;
      }

      let cancelled = false;
      const loadNotifications = async () => {
        try {
          const response = await fetch(`/api/organizations/${selectedOrgId}/notifications`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!response.ok) throw new Error('Failed to load notifications');
          const rows = await response.json();
          if (!cancelled && Array.isArray(rows)) applyLoadedNotifications(rows);
        } catch (error) {
          if (!cancelled) {
            console.warn('Local notifications load failed:', error);
            setNotifications([]);
          }
        }
      };
      void loadNotifications();
      const notificationTimer = window.setInterval(loadNotifications, 30000);
      return () => {
        cancelled = true;
        window.clearInterval(notificationTimer);
      };
    }

    // Local PostgreSQL session: fetch documents, notifications, and users once.
    // The shim's polling onSnapshot is not wired here because the server exposes
    // typed /api/organizations/:orgId/* routes only.
    const token = localStorage.getItem(LOCAL_API_TOKEN_KEY);
    if (!token) {
      setTransactions([]);
      setInvoices([]);
      setBills([]);
      setUsers([]);
      knownNotificationIdsRef.current = null;
      setNotifications([]);
      return;
    }

    const headers = { Authorization: `Bearer ${token}` };
    let cancelled = false;

    fetch(`/api/organizations/${selectedOrgId}/documents?types=invoice`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then((rows: any[]) => { if (!cancelled) setInvoices(rows); })
      .catch(() => setInvoices([]));

    fetch(`/api/organizations/${selectedOrgId}/documents?types=bill`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then((rows: any[]) => { if (!cancelled) setBills(rows); })
      .catch(() => setBills([]));

    fetch(`/api/organizations/${selectedOrgId}/users`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then((rows: any[]) => { if (!cancelled) setUsers(rows); })
      .catch(() => setUsers([]));

    const loadNotifications = async () => {
      try {
        const response = await fetch(`/api/organizations/${selectedOrgId}/notifications`, { headers });
        if (!response.ok) throw new Error(`Notification request failed (${response.status})`);
        const rows = await response.json();
        if (!cancelled && Array.isArray(rows)) applyLoadedNotifications(rows);
      } catch (error) {
        if (!cancelled) {
          console.warn('Notifications load failed:', error);
          setNotifications([]);
        }
      }
    };
    void loadNotifications();
    const notificationTimer = window.setInterval(loadNotifications, 30000);

    setTransactions([]);
    return () => {
      cancelled = true;
      window.clearInterval(notificationTimer);
    };
  }, [user, isAuthReady, selectedOrgId]);

  // Calculate stats whenever data changes
  useEffect(() => {
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();
    const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
    const lastMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;

    let income = 0;
    let expenses = 0;
    let thisMonthRevenue = 0;
    let lastMonthRevenue = 0;
    let thisMonthExpenses = 0;
    let lastMonthExpenses = 0;

    transactions.forEach((tx: any) => {
      if (tx.amount > 0) income += tx.amount;
      else expenses += Math.abs(tx.amount);
    });

    invoices.forEach((inv: any) => {
      const invDate = new Date(inv.date);
      const amount = inv.totalAmount || 0;
      if (invDate.getMonth() === thisMonth && invDate.getFullYear() === thisYear) {
        thisMonthRevenue += amount;
      } else if (invDate.getMonth() === lastMonth && invDate.getFullYear() === lastMonthYear) {
        lastMonthRevenue += amount;
      }
    });

    bills.forEach((bill: any) => {
      const billDate = new Date(bill.date);
      const amount = bill.totalAmount || 0;
      if (billDate.getMonth() === thisMonth && billDate.getFullYear() === thisYear) {
        thisMonthExpenses += amount;
      } else if (billDate.getMonth() === lastMonth && billDate.getFullYear() === lastMonthYear) {
        lastMonthExpenses += amount;
      }
    });

    const billsAwaitingPayment = bills.filter(b => b.status === 'Awaiting Payment').length;
    const invoicesAwaitingPayment = invoices.filter(i => i.status === 'Awaiting Payment').length;

    setStats({ 
      balance: income - expenses, 
      income, 
      expenses,
      thisMonthRevenue,
      lastMonthRevenue,
      thisMonthExpenses,
      lastMonthExpenses,
      billsAwaitingPayment,
      invoicesAwaitingPayment
    });
  }, [transactions, invoices, bills]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsAnalyzing(true);
    setAnalysisResult(null);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target?.result as string;
        try {
          const result = await analyzeAccountingImage(base64, file.type);
          setAnalysisResult(result);
        } catch (error) {
          console.error("Analysis failed:", error);
          notify.error("Analysis failed: " + (error instanceof Error ? error.message : "Unknown error"));
        } finally {
          setIsAnalyzing(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Analysis failed:", error);
      setIsAnalyzing(false);
    }
  };

  const handleAddTransaction = async () => {
    if (!user || !selectedOrgId) return;
    try {
      await addDoc(collection(db, 'transactions'), {
        date: new Date().toISOString(),
        description: 'Sample Transaction',
        category: 'General',
        amount: Math.random() > 0.5 ? 500 : -200,
        status: 'Completed',
        createdBy: user.uid,
        orgId: selectedOrgId,
        createdAt: serverTimestamp()
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'transactions');
    }
  };

  const translations = {
    en: {
      dashboard: 'Dashboard',
      sales: 'Sales',
      quotes: 'Quotes',
      invoices: 'Invoices',
      deliveries: 'Deliveries',
      pos: 'POS',
      pos_sell: 'Sell Screen',
      pos_history: 'Sales History',
      catalog: 'Catalog',
      stores: 'Stores',
      purchases: 'Purchases',
      purchase_orders: 'Purchase Orders',
      bills: 'Bills',
      purchase_deliveries: 'Deliveries',
      expense_submissions: 'Expense Submissions',
      landed_costs: 'Landed Costs',
      upload_expense_bills: 'Upload Expense Bills',
      contacts: 'Contacts',
      all_contacts: 'All Contacts',
      customers: 'Customers',
      suppliers: 'Suppliers',
      items: 'Inventory',
      products_services: 'Products & Services',
      inventory_reports: 'Inventory Reports',
      inventory_manager: 'Inventory Manager',
      categories: 'Category / Family / UOM',
      payroll: 'HR',
      payrun: 'PayRun',
      employees: 'Employees',
      payrun_settings: 'Payrun Settings',
      attendance: 'Check In and Check Out',
      tasks_mops: 'Task/Activities',
      reports: 'Reports',
      accounting: 'Accounting',
      chart_of_accounts: 'Chart of Accounts',
      bank_accounts: 'Cash & Bank',
      fixed_assets: 'Fixed Assets',
      manual_journal: 'Manual Journal',
      customer_balances: 'Customer Balances',
      vendor_balances: 'Vendor Balances',
      trial_balance: 'Trial Balance',
      discussions: 'Discussions',
      tracking_categories: 'Tracking Categories',
      tax_base: 'Tax Base',
      tags: 'Tags',
      help: 'Help & How To',
      settings: 'Settings',
      logout: 'Logout',
      profile: 'Profile',
      financial_overview: 'Financial Overview',
      real_time_accounting: 'Real-time accounting and ledger management',
      new_transaction: 'New Transaction',
      total_balance: 'Total Balance',
      monthly_income: 'Monthly Income',
      monthly_expenses: 'Monthly Expenses',
      cash_flow: 'Cash Flow Analysis',
      recent_transactions: 'Recent Transactions',
      view_all: 'View All Transactions',
      no_transactions: 'No transactions found.',
      coming_soon: 'Module is coming soon...',
      accountex: 'Accountix',
      signin_msg: 'Sign in with Google for a passwordless experience, or enter your email for direct access.',
      signin_google: 'Sign in with Google (Passwordless)',
      signin_email: 'Or continue with email',
      email_placeholder: 'name@company.com',
      login_directly: 'Login Directly',
      email_label: 'Email Address',
      license_key_label: 'License Key',
      or_use_license: 'Or Use License Key',
      activate_license: 'Activate License',
      license_placeholder: 'XXXX-XXXX-XXXX-XXXX',
      dark_mode: 'Dark Mode',
      light_mode: 'Light Mode'
    },
    ar: {
      dashboard: 'لوحة القيادة',
      sales: 'المبيعات',
      quotes: 'العروض',
      invoices: 'الفواتير',
      deliveries: 'التسليمات',
      pos: 'نقطة البيع',
      pos_sell: 'شاشة البيع',
      pos_history: 'سجل المبيعات',
      catalog: 'الكتالوج',
      stores: 'المتاجر',
      purchases: 'المشتريات',
      purchase_orders: 'أوامر الشراء',
      bills: 'الفواتير',
      purchase_deliveries: 'الاستلامات',
      expense_submissions: 'تقديم المصاريف',
      landed_costs: 'التكاليف الإضافية',
      upload_expense_bills: 'تحميل فواتير المصاريف',
      contacts: 'جهات الاتصال',
      all_contacts: 'جميع جهات الاتصال',
      customers: 'العملاء',
      suppliers: 'الموردين',
      items: 'المخزون',
      products_services: 'المنتجات والخدمات',
      inventory_reports: 'تقارير المخزون',
      inventory_manager: 'مدير المخزون',
      categories: 'الفئات / العائلات / وحدات القياس',
      payroll: 'الموارد البشرية',
      payrun: 'تشغيل الرواتب',
      employees: 'الموظفين',
      payrun_settings: 'إعدادات الرواتب',
      attendance: 'تسجيل الحضور والانصراف',
      tasks_mops: 'المهام / الأنشطة',
      reports: 'التقارير',
      accounting: 'المحاسبة',
      chart_of_accounts: 'دليل الحسابات',
      bank_accounts: 'النقد والبنك',
      fixed_assets: 'الأصول الثابتة',
      manual_journal: 'القيود اليدوية',
      customer_balances: 'أرصدة العملاء',
      vendor_balances: 'أرصدة الموردين',
      trial_balance: 'ميزان المراجعة',
      discussions: 'المناقشات',
      tracking_categories: 'فئات التتبع',
      tax_base: 'قاعدة الضرائب',
      tags: 'الوسوم',
      help: 'المساعدة وكيفية الاستخدام',
      settings: 'الإعدادات',
      logout: 'تسجيل الخروج',
      profile: 'الملف الشخصي',
      financial_overview: 'نظرة عامة مالية',
      real_time_accounting: 'المحاسبة في الوقت الفعلي وإدارة الدفاتر',
      new_transaction: 'عملية جديدة',
      total_balance: 'إجمالي الرصيد',
      monthly_income: 'الدخل الشهري',
      monthly_expenses: 'المصاريف الشهرية',
      cash_flow: 'تحليل التدفق النقدي',
      recent_transactions: 'العمليات الأخيرة',
      view_all: 'عرض كل العمليات',
      no_transactions: 'لا توجد عمليات.',
      coming_soon: 'الوحدة ستتوفر قريباً...',
      accountex: 'Accountix',
      signin_msg: 'سجل الدخول باستخدام جوجل لتجربة بدون كلمة مرور، أو أدخل بريدك الإلكتروني للوصول المباشر.',
      signin_google: 'تسجيل الدخول باستخدام جوجل (بدون كلمة مرور)',
      signin_email: 'أو المتابعة باستخدام البريد الإلكتروني',
      email_placeholder: 'name@company.com',
      login_directly: 'تسجيل الدخول مباشرة',
      email_label: 'البريد الإلكتروني',
      license_key_label: 'مفتاح الترخيص',
      or_use_license: 'أو استخدم مفتاح الترخيص',
      activate_license: 'تفعيل الترخيص',
      license_placeholder: 'XXXX-XXXX-XXXX-XXXX',
      dark_mode: 'الوضع الداكن',
      light_mode: 'الوضع المضيء'
    }
  };

  const [initialAccountId, setInitialAccountId] = useState<string | null>(null);
  const [initialStartDate, setInitialStartDate] = useState<any>(null);
  const [initialEndDate, setInitialEndDate] = useState<any>(null);

  const handleClearInitial = React.useCallback(() => {
    // Keep the deep-link ID in the URL after the view consumes it. This makes
    // the current document remain refreshable and shareable.
    setInitialAccountId(null);
    setInitialStartDate(null);
    setInitialEndDate(null);
  }, []);

  const handleOrganizationChange = React.useCallback(async (orgId: string) => {
    if (!orgId || orgId === selectedOrgId) return;

    const token = localStorage.getItem(LOCAL_API_TOKEN_KEY);
    if (!token && isLocalDevUser(user)) {
      setSelectedOrgId(orgId);
      setShowAIChat(false);
      return;
    }

    if (!token) {
      notify.error('Your session has expired. Please sign in again.');
      return;
    }

    try {
      const response = await fetch('/api/auth/select-organization', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgId }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.token) throw new Error(payload?.error || 'Organization switch failed');

      localStorage.setItem(LOCAL_API_TOKEN_KEY, payload.token);
      localStorage.setItem('selected_org_id', payload.orgId || orgId);
      setShowAIChat(false);
      setSelectedOrgId(payload.orgId || orgId);
      notify.confirm(`Switched to ${organizations.find(organization => organization.id === (payload.orgId || orgId))?.name || 'organization'}`);
    } catch (error: any) {
      notify.error(error?.message || 'Could not switch organization');
    }
  }, [organizations, selectedOrgId, user]);

  const handleNavigate = React.useCallback((tab: string, id?: string | null, extra?: any) => {
    let targetTab = tab;
    // Map legacy products/services inventory access to inventory_manager
    if (tab === 'items' || tab === 'inventory') {
      targetTab = 'inventory_manager';
    }
    setActiveTab(targetTab as any);
    setSelectedEntityId(id || null);
    updateAppHash(targetTab, id);
    if (extra && extra.accountId) setInitialAccountId(extra.accountId);
    if (extra && extra.startDate) setInitialStartDate(extra.startDate);
    if (extra && extra.endDate) setInitialEndDate(extra.endDate);
  }, [updateAppHash]);

  const t = translations[language];

  const themes = {
    classic: {
      bg: "bg-background",
      text: "text-foreground",
      border: "border-border",
      sidebar: "bg-background",
      header: "bg-background/80",
      card: "bg-card shadow-sm",
      accent: "bg-primary",
      accentText: "text-primary-foreground",
      font: "font-sans",
      rounded: "rounded-xl",
      vars: {
        '--primary': '#0284c7',
        '--primary-foreground': '#ffffff',
        '--background': '#f8fafc',
        '--card': '#ffffff',
        '--border': '#e2e8f0',
        '--secondary': '#f1f5f9',
        '--secondary-foreground': '#0f172a',
        '--muted': '#f1f5f9',
        '--muted-foreground': '#64748b',
        '--destructive': '#ef4444',
        '--destructive-foreground': '#ffffff'
      }
    },
    modern: {
      bg: "bg-background",
      text: "text-foreground",
      border: "border-border",
      sidebar: "bg-background",
      header: "bg-background/80",
      card: "bg-card shadow-sm",
      accent: "bg-primary",
      accentText: "text-primary-foreground",
      font: "font-sans",
      rounded: "rounded-xl",
      vars: {
        '--primary': '#2563eb',
        '--primary-foreground': '#ffffff',
        '--background': '#f8fafc',
        '--card': '#ffffff',
        '--border': '#e2e8f0',
        '--secondary': '#f1f5f9',
        '--secondary-foreground': '#0f172a',
        '--muted': '#f8fafc',
        '--muted-foreground': '#64748b',
        '--destructive': '#ef4444',
        '--destructive-foreground': '#ffffff'
      }
    },
    brutalist: {
      bg: "bg-background",
      text: "text-foreground",
      border: "border-4 border-border",
      sidebar: "bg-background",
      header: "bg-background",
      card: "bg-card border-4 border-border shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]",
      accent: "bg-primary",
      accentText: "text-primary-foreground",
      font: "font-mono",
      rounded: "rounded-none",
      vars: {
        '--primary': '#000000',
        '--primary-foreground': '#ffffff',
        '--background': '#ffffff',
        '--card': '#ffffff',
        '--border': '#000000',
        '--secondary': '#f1f1f1',
        '--secondary-foreground': '#000000',
        '--destructive': '#ff0000',
        '--destructive-foreground': '#ffffff',
        '--muted': '#f1f1f1',
        '--muted-foreground': '#666666'
      }
    },
    'pastel-greenery': {
      bg: "bg-background",
      text: "text-foreground",
      border: "border-border",
      sidebar: "bg-secondary",
      header: "bg-secondary/20 backdrop-blur-md",
      card: "bg-card border border-border shadow-xl backdrop-blur-sm",
      accent: "bg-primary",
      accentText: "text-primary-foreground",
      font: "font-sans",
      rounded: "rounded-[2.5rem]",
      vars: {
        '--primary': '#14746F',
        '--primary-foreground': '#ffffff',
        '--background': '#F2F7F2',
        '--card': 'rgba(255, 255, 255, 0.8)',
        '--border': 'rgba(86, 171, 145, 0.3)',
        '--foreground': '#104547',
        '--muted': '#F2F7F2',
        '--muted-foreground': 'rgba(16, 69, 71, 0.6)',
        '--secondary': '#E8F3E8',
        '--secondary-foreground': '#104547',
        '--destructive': '#ef4444',
        '--destructive-foreground': '#ffffff'
      }
    },
    'cherry-blossom': {
      bg: "bg-background",
      sidebar: "bg-secondary/40 border-r border-border backdrop-blur-xl",
      text: "text-foreground",
      accent: "bg-primary shadow-lg shadow-primary/20",
      accentText: "text-primary-foreground",
      border: "border-border",
      card: "bg-card border border-border shadow-pink-100/50 shadow-xl",
      rounded: "rounded-[24px]",
      font: "font-serif",
      vars: {
        '--primary': '#A4133C', // Spring Fuchsia
        '--primary-foreground': '#ffffff',
        '--background': '#FFF0F3', // Soft Blush
        '--card': 'rgba(255, 255, 255, 0.85)',
        '--border': '#FFCCD5', // Soft Pink Border
        '--foreground': '#590D22', // Night Bordeaux
        '--secondary': '#FFB3C1', // Sakura Petal
        '--secondary-foreground': '#590D22',
        '--muted': '#FFF0F3',
        '--muted-foreground': '#800F2F'
      }
    },
    'minimal': {
      bg: "bg-background",
      sidebar: "bg-background border-r border-border",
      text: "text-foreground",
      accent: "bg-primary",
      accentText: "text-primary-foreground",
      border: "border-border",
      card: "bg-card border border-border shadow-sm",
      rounded: "rounded-none",
      font: "font-sans",
      vars: {
        '--primary': '#1A1A1A',
        '--primary-foreground': '#FFFFFF',
        '--background': '#F9F9F9',
        '--card': '#FFFFFF',
        '--border': '#E5E5E5',
        '--foreground': '#1A1A1A',
        '--secondary': '#F0F0F0',
        '--secondary-foreground': '#1A1A1A',
        '--muted': '#F9F9F9',
        '--muted-foreground': '#737373'
      }
    },
    'technical': {
      bg: "bg-background",
      sidebar: "bg-secondary border-r border-border",
      text: "text-foreground",
      accent: "bg-primary shadow-[0_0_15px_rgba(34,197,94,0.3)]",
      accentText: "text-primary-foreground",
      border: "border-border",
      card: "bg-card border border-border shadow-inner",
      rounded: "rounded-lg",
      font: "font-mono",
      vars: {
        '--primary': '#22C55E', // Green-500
        '--primary-foreground': '#052E16',
        '--background': '#000000', // Pure black
        '--card': '#000000', // Pure black
        '--border': '#334155', // Slate-700
        '--foreground': '#F8FAFC', // Slate-50
        '--secondary': '#0A0A0A',
        '--secondary-foreground': '#94A3B8',
        '--muted': '#111111',
        '--muted-foreground': '#64748B'
      }
    }
  };

  const theme = themes[layoutPreference] || themes.classic;

  // Track viewport size so the sidebar can switch to an overlay drawer on mobile.
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    handleResize(); // keep in sync if SSR were ever used
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // When crossing the lg breakpoint, restore desktop's expanded default so the
  // fixed `pl-64` reservation doesn't leave an empty gutter after a refresh.
  useEffect(() => {
    if (!isMobile && !isSidebarOpen) setIsSidebarOpen(true);
    if (isMobile && isSidebarOpen) setIsSidebarOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  // Auto-collapse the mobile drawer whenever the active view changes so users
  // see their selection without having to manually close the menu.
  useEffect(() => {
    if (isMobile) setIsSidebarOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Keep the document root as the single source of truth for the active palette.
  useEffect(() => {
    localStorage.setItem('isDarkMode', isDarkMode.toString());
    const root = document.documentElement;
    const currentTheme = (themes as any)[layoutPreference] || themes.classic;

    root.classList.toggle('dark', isDarkMode);
    root.style.colorScheme = isDarkMode ? 'dark' : 'light';

    // Theme presets are written inline for light mode. Clear every preset token
    // before switching modes so those inline values cannot override `.dark` CSS.
    THEME_VARIABLE_KEYS.forEach(key => root.style.removeProperty(key));
    const useCssDarkPalette = isDarkMode && layoutPreference !== 'technical';
    if (currentTheme.vars && !useCssDarkPalette) {
      Object.entries(currentTheme.vars).forEach(([key, value]) => {
        root.style.setProperty(key, value as string);
      });
    }

    const themeColor = document.querySelector('meta[name="theme-color"]');
    themeColor?.setAttribute('content', isDarkMode ? '#090d16' : '#002b49');
  }, [layoutPreference, isDarkMode]);

  const isSuperAdmin = isSuperAdminEmail(user?.email)
    || ['super_admin', 'super-admin'].includes(user?.role)
    || ['super_admin', 'super-admin'].includes(currentOrgMembership?.role);

  const hasPermission = (moduleId: string) => {
    if (isSuperAdmin) return true;
    if (moduleId === 'help' || moduleId === 'profile' || moduleId === 'dashboard') return true;
    
    // Check if logged in via License Key
    if (licenseKey && !user) {
      if (moduleId === 'super_admin') return false; // License users can NEVER be super admins

      try {
        const storedPerms = localStorage.getItem('app_license_permissions');
        if (storedPerms) {
          const permsArray: string[] = JSON.parse(storedPerms);
          if (permsArray.includes('all')) return true;
          if (permsArray.includes(moduleId)) return true;
          
          // Check if parent module is allowed
          const module = MODULES.find(m => m.id === moduleId || m.subModules.some(sm => sm.id === moduleId));
          if (module && permsArray.includes(module.id)) return true;

          return false;
        }
      } catch (e) {
        // error parsing
      }
      return true; // Default fallback if no permissions found
    }

    if (!currentOrgMembership) return false;
    
    // Check if organization has this module assigned
    const currentOrg = organizations.find(o => o.id === selectedOrgId);
    if (currentOrg && currentOrg.assignedModules && currentOrg.assignedModules.length > 0) {
      // Find the module that contains this moduleId (either as module id or sub-module id)
      const module = MODULES.find(m => m.id === moduleId || m.subModules.some(sm => sm.id === moduleId));
      
      if (module) {
        // If it's a top-level module check, check if the module itself is assigned
        if (module.id === moduleId) {
          if (!currentOrg.assignedModules.includes(moduleId)) {
             return false;
          }
        } else {
          // If it's a sub-module check, check if either the module OR the sub-module is assigned
          if (!currentOrg.assignedModules.includes(module.id) && !currentOrg.assignedModules.includes(moduleId)) {
            return false;
          }
        }
      }
    }

    if (currentOrgMembership.role === 'super-admin' || currentOrgMembership.role === 'owner') return true;
    
    // Admin gets a subset of permissions
    if (currentOrgMembership.role === 'admin') {
      const restrictedModules = ['settings', 'super_admin'];
      return !restrictedModules.includes(moduleId);
    }
    
    // Check for granular permissions
    if (currentOrgMembership.permissions?.includes(`${moduleId}.edit`)) return true;
    if (currentOrgMembership.permissions?.includes(`${moduleId}.view`)) return true;
    
    return currentOrgMembership.permissions?.includes(moduleId);
  };

  const handleEnableBrowserNotifications = React.useCallback(async () => {
    if (!isBrowserNotificationSupported()) return;
    await requestBrowserNotificationPermission();
  }, []);

  const handleNotificationClick = async (notification: any) => {
    try {
      const token = localStorage.getItem(LOCAL_API_TOKEN_KEY);
      if (token && selectedOrgId) {
        const response = await fetch(`/api/organizations/${selectedOrgId}/notifications/${notification.id}`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ read: true }),
        });
        if (!response.ok) throw new Error('Could not mark notification as read');
      } else if (notification.id) {
        await updateDoc(doc(db, 'notifications', notification.id), { read: true, readAt: serverTimestamp() });
      }
      setNotifications(currentNotifications => currentNotifications.map(currentNotification =>
        currentNotification.id === notification.id
          ? { ...currentNotification, read: true }
          : currentNotification
      ));
      const relatedTypeTabs: Record<string, string> = {
        Quote: 'quotes', Invoice: 'invoices', Delivery: 'deliveries', PurchaseOrder: 'purchase_orders',
        Bill: 'bills', Task: 'tasks_mops', Contact: 'contacts', Product: 'inventory_manager',
      };
      const linkedTab = notification.relatedType ? relatedTypeTabs[notification.relatedType] : undefined;
      const linkParts = typeof notification.link === 'string' ? notification.link.split('/').filter(Boolean) : [];
      const targetTab = linkedTab || linkParts[0];
      const targetId = notification.relatedId || linkParts[1];
      if (targetTab) {
        handleNavigate(targetTab, targetId);
      } else if (notification.type?.startsWith('task_')) {
        handleNavigate('tasks_mops', targetId);
      } else if (notification.type === 'mention' || notification.type === 'discussion') {
        handleNavigate('discussions', notification.threadId || targetId);
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleMarkAllNotificationsRead = React.useCallback(async () => {
    try {
      const token = localStorage.getItem(LOCAL_API_TOKEN_KEY);
      if (!selectedOrgId) return;
      if (!token) {
        await Promise.all(notifications.filter(notification => !notification.read && notification.id).map(notification =>
          updateDoc(doc(db, 'notifications', notification.id), { read: true, readAt: serverTimestamp() })
        ));
      } else {
        const response = await fetch(`/api/organizations/${selectedOrgId}/notifications/mark-all-read`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Could not mark all notifications as read');
      }
      const readAt = new Date().toISOString();
      setNotifications(current => current.map(notification => ({ ...notification, read: true, readAt })));
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      notify.error(language === 'ar' ? 'تعذر تحديد الإشعارات كمقروءة' : 'Could not mark notifications as read', {
        retry: () => void handleMarkAllNotificationsRead(),
      });
    }
  }, [language, notifications, selectedOrgId]);

  if (!isAuthReady) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin dark:text-white" />
      </div>
    );
  }

  if (portalQuoteId) {
    return <PublicQuotePortal quoteId={portalQuoteId} />;
  }

  if (portalInvoiceId) {
    return <PublicInvoicePortal invoiceId={portalInvoiceId} />;
  }

  if (portalBillId) {
    return <PublicDocumentPortal documentId={portalBillId} type="bill" />;
  }

  if (portalPurchaseOrderId) {
    return <PublicDocumentPortal documentId={portalPurchaseOrderId} type="purchase_order" />;
  }

  if (menuProductId) {
    return <DigitalMenuProductPage id={menuProductId} />;
  }

  if (publicCatalogOrgId) {
    return <PublicCatalogView orgId={publicCatalogOrgId} />;
  }

  if (!user && !licenseKey) {
    return (
      <div className={cn("min-h-screen relative editorial-mesh editorial-grain flex flex-col items-center justify-center p-8 overflow-hidden", theme.bg, theme.text, theme.font)} dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <Toaster position="top-right" theme={isDarkMode ? 'dark' : 'light'} />
        {/* Decorative orbs for depth */}
        <div aria-hidden className="absolute -top-24 -start-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl pointer-events-none editorial-fade" />
        <div aria-hidden className="absolute -bottom-32 -end-20 w-[28rem] h-[28rem] rounded-full bg-primary/5 blur-3xl pointer-events-none editorial-fade" style={{ animationDelay: '0.15s' }} />

        <div className={cn("relative z-10 border p-12 text-center max-w-md w-full editorial-card editorial-rise", theme.card, theme.rounded, theme.border)} style={{ animationDelay: '0.1s' }}>
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center shadow-lg shadow-primary/30">
              <Wallet className="w-8 h-8" />
            </div>
          </div>
          <h1 className="font-display text-5xl font-extrabold mb-3 tracking-tight">{t.accountex}</h1>
          <p className="text-sm opacity-50 italic serif mb-10 max-w-xs mx-auto leading-relaxed">{t.signin_msg}</p>
          <button 
            onClick={async () => {
              const email = emailInput.trim();
              if (!email) {
                notify.error('Enter your email above to sign in.');
                return;
              }
              try {
                const response = await fetch('/api/auth/login', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email, password: 'password123' }),
                });
                const payload = await response.json();
                if (!response.ok) throw new Error(payload.error || 'Failed to log in');
                localStorage.setItem(LOCAL_API_TOKEN_KEY, payload.token);
                if (payload.user?.orgId) localStorage.setItem('selected_org_id', payload.user.orgId);
                window.location.reload();
              } catch (err: any) {
                notify.error(err.message || 'Login failed');
              }
            }}
            className={cn("w-full flex items-center justify-center gap-3 py-4 font-bold hover:opacity-90 transition-opacity", theme.accent, theme.accentText, theme.rounded)}
          >
            <LogIn className="w-5 h-5" />
            {t.signin_google}
          </button>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-muted"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase tracking-[0.2em]">
              <span className={cn("px-3 opacity-70", theme.card)}>{t.signin_email}</span>
            </div>
          </div>

          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div className="space-y-2 text-left">
              <label className="text-xs font-bold uppercase opacity-60">Username or Email</label>
              <input 
                type="text"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="Enter username or email"
                className={cn("w-full px-4 py-3 border outline-none focus:ring-2 focus:ring-primary/20 transition-all", theme.rounded, theme.border)}
                required
              />
            </div>

            <div className="space-y-2 text-left">
              <label className="text-xs font-bold uppercase opacity-60">Password</label>
              <div className="relative">
                <input 
                  type={showPassword ? "text" : "password"}
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  placeholder="Enter password"
                  className={cn("w-full px-4 py-3 pr-10 border outline-none focus:ring-2 focus:ring-primary/20 transition-all", theme.rounded, theme.border)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button 
              type="submit"
              disabled={isLoggingIn}
              className={cn("w-full flex items-center justify-center gap-3 py-4 font-bold hover:opacity-90 transition-opacity disabled:opacity-50", theme.accent, theme.accentText, theme.rounded)}
            >
              {isLoggingIn ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogIn className="w-5 h-5" />}
              {t.login_directly || 'Sign In'}
            </button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-muted"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase tracking-[0.2em]">
              <span className={cn("px-3 opacity-70", theme.card)}>{t.or_use_license}</span>
            </div>
          </div>

          <form onSubmit={handleLicenseLogin} className="space-y-4">
            <div className="space-y-2 text-left">
              <label className="text-xs font-bold uppercase opacity-50">{t.license_key_label || 'License Key'}</label>
              <input 
                type="text"
                value={licenseInput}
                onChange={(e) => setLicenseInput(e.target.value)}
                placeholder={t.license_placeholder}
                className={cn("w-full px-4 py-3 border font-mono tracking-wider outline-none focus:ring-2 focus:ring-primary/20 transition-all", theme.rounded, theme.border)}
                required
              />
            </div>
            <button 
              type="submit"
              disabled={isActivating}
              className={cn("w-full flex items-center justify-center gap-3 py-4 font-bold hover:opacity-90 transition-opacity disabled:opacity-50 border", theme.rounded, theme.border)}
            >
              {isActivating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Key className="w-5 h-5" />}
              {t.activate_license}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (user && user.mustChangePassword) {
    const handleFirstLoginPasswordChange = async (event: React.FormEvent) => {
      event.preventDefault();
      if (newPassword.length < 8) {
        notify.error('New password must be at least 8 characters');
        return;
      }
      if (newPassword !== confirmNewPassword) {
        notify.error('Passwords do not match');
        return;
      }
      setIsChangingPassword(true);
      try {
        const token = localStorage.getItem(LOCAL_API_TOKEN_KEY);
        const response = await fetch('/api/auth/change-password', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ newPassword }),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) throw new Error(payload?.error || 'Could not change password');
        notify.confirm('Password updated successfully');
        window.location.reload();
      } catch (error: any) {
        notify.error(error.message || 'Could not change password');
      } finally {
        setIsChangingPassword(false);
      }
    };

    return (
      <div className={cn("min-h-screen flex items-center justify-center p-4", theme.bg, theme.text, theme.font)}>
        <form onSubmit={handleFirstLoginPasswordChange} className={cn("w-full max-w-md space-y-5 border p-8", theme.card, theme.rounded, theme.border)}>
          <h1 className="text-2xl font-bold">Create your new password</h1>
          <p className="text-sm opacity-70">For your security, choose a new password before continuing. This is required only once.</p>
          <input type="password" value={newPassword} onChange={event => setNewPassword(event.target.value)} placeholder="New password" autoComplete="new-password" className={cn("w-full px-4 py-3 border outline-none", theme.rounded, theme.border)} required />
          <input type="password" value={confirmNewPassword} onChange={event => setConfirmNewPassword(event.target.value)} placeholder="Confirm new password" autoComplete="new-password" className={cn("w-full px-4 py-3 border outline-none", theme.rounded, theme.border)} required />
          <button type="submit" disabled={isChangingPassword} className={cn("w-full py-3 font-bold disabled:opacity-50", theme.accent, theme.accentText, theme.rounded)}>
            {isChangingPassword ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Set new password'}
          </button>
        </form>
      </div>
    );
  }

  if (user && organizations.length === 0 && !isSuperAdmin) {
    return (
      <div className={cn("min-h-screen flex flex-col items-center justify-center p-4 text-center space-y-6", theme.bg, theme.text, theme.font)}>
        <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center text-destructive">
          <ShieldCheck className="w-10 h-10" />
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-bold">Access Restricted</h1>
          <p className="text-muted-foreground max-w-md mx-auto">
            Your account is either suspended or you haven't been assigned to any organization yet. 
            Please contact your administrator for access.
          </p>
        </div>
        <button 
          onClick={() => {
            localStorage.removeItem(LOCAL_API_TOKEN_KEY);
            localStorage.removeItem(LOCAL_DEV_USER_EMAIL_KEY);
            localStorage.removeItem('selected_org_id');
            window.location.reload();
          }}
          className={cn("px-6 py-2 font-bold uppercase text-xs", theme.accent, theme.accentText, theme.rounded)}
        >
          Sign Out
        </button>
      </div>
    );
  }

  return (
    <LicenseGate>
      <div
        className={cn("app-shell min-h-screen w-full max-w-full overflow-x-hidden relative editorial-mesh selection:bg-sky-500 selection:text-white", theme.bg, theme.text, theme.font)}
        dir={language === 'ar' ? 'rtl' : 'ltr'}
        style={{ '--app-sidebar-width': `${sidebarWidth}px` } as React.CSSProperties}
      >
      {isOffline && (
        <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white text-[10px] font-bold py-1 px-4 z-[100] flex items-center justify-center gap-2 animate-in slide-in-from-top duration-300">
          <ShieldCheck className="w-3 h-3" />
          {language === 'en' ? 'Working Offline - Changes will sync when connection is restored' : 'تعمل في وضع عدم الاتصال - ستتم مزامنة التغييرات عند استعادة الاتصال'}
        </div>
      )}
      <Toaster position="bottom-center" visibleToasts={1} duration={2000} theme={isDarkMode ? 'dark' : 'light'} />
      <FinancialNotificationsChecker user={user} orgId={selectedOrgId} />
      <AppNavigation
        organizations={organizations}
        selectedOrgId={selectedOrgId}
        onOrganizationChange={handleOrganizationChange}
        activeTab={activeTab}
        onNavigate={handleNavigate}
        expandedMenus={expandedMenus}
        onToggleMenu={(moduleId) => setExpandedMenus(previous => ({ ...previous, [moduleId]: !previous[moduleId] }))}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
        isMobile={isMobile}
        isDarkMode={isDarkMode}
        onToggleDarkMode={() => setIsDarkMode(previous => !previous)}
        profile={profile}
        user={user}
        notifications={notifications}
        onNotificationClick={handleNotificationClick}
        onMarkAllNotificationsRead={handleMarkAllNotificationsRead}
        onEnableBrowserNotifications={handleEnableBrowserNotifications}
        onLogout={() => {
          localStorage.removeItem(LOCAL_DEV_USER_EMAIL_KEY);
          localStorage.removeItem(LOCAL_API_TOKEN_KEY);
          localStorage.removeItem('selected_org_id');
          window.location.reload();
        }}
        language={language}
        localDevOrgId={LOCAL_DEV_ORG_ID}
        isSuperAdmin={isSuperAdmin}
      />


      <SecurityMonitor user={user} orgId={selectedOrgId} activeTab={activeTab} />

      {/* Main Content Area */}
      <main className={cn("app-main min-h-screen print:px-0 max-w-full overflow-x-hidden px-4 sm:px-6 lg:px-8", !isMobile && (isSidebarOpen ? "lg:ms-64" : "lg:ms-[4.5rem]"))}>
        {/* Content Area */}
        <div className="mx-auto w-full max-w-[1600px] overflow-x-hidden px-0 pb-12 safe-x">
          {isAnalyzing && (
            <div className={cn("mb-8 p-12 border-2 border-dashed rounded-2xl flex flex-col items-center justify-center animate-pulse", theme.border, theme.card)}>
              <Loader2 className="w-12 h-12 animate-spin mb-4" />
              <p className="text-xl font-bold italic serif">{language === 'ar' ? 'جاري تحليل لقطة شاشة النظام المحاسبي...' : 'Analyzing your accounting system screenshot...'}</p>
              <p className="text-sm opacity-50 mt-2">{language === 'ar' ? 'يجري استخراج الميزات وتفاصيل التخطيط' : 'Gemini is extracting features and layout details'}</p>
            </div>
          )}

          {analysisResult && (
            <div className={cn("mb-8 p-8 border rounded-2xl", theme.border, theme.card)}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  {language === 'ar' ? 'نتائج تحليل الذكاء الاصطناعي' : 'AI Analysis Results'}
                </h2>
                <button 
                  onClick={() => setAnalysisResult(null)}
                  className="p-1 hover:bg-black/5 transition-colors rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="prose prose-sm max-w-none text-inherit">
                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed opacity-80">
                  {analysisResult}
                </pre>
              </div>
              <button className={cn("mt-6 px-6 py-3 rounded-full hover:opacity-90 transition-opacity font-bold", theme.accent, theme.accentText)}>
                {language === 'ar' ? 'تطبيق توصيات الذكاء الاصطناعي' : 'Apply AI Recommendations'}
              </button>
            </div>
          )}

          
          {activeTab === 'quotes' ? (
            <QuotesView  orgId={selectedOrgId} language={language}
              initialQuoteId={selectedEntityId}
              onClearInitial={handleClearInitial}
              onNavigate={handleNavigate} 
            />
          ) : activeTab === 'invoices' ? (
            <InvoicesView  orgId={selectedOrgId} orgSettings={orgSettings} language={language}
              
              initialInvoiceId={selectedEntityId}
              onClearInitial={handleClearInitial}
              onNavigate={handleNavigate}
            />
          ) : activeTab === 'deliveries' ? (
            <DeliveriesView  orgId={selectedOrgId} language={language}
              initialDeliveryId={selectedEntityId}
              onClearInitial={handleClearInitial}
            />
          ) : activeTab === 'items' ? (
            <ItemsView  orgId={selectedOrgId} language={language}
              initialFilter="reporting"
              isReportsMode={true}
              initialItemId={selectedEntityId} onClearInitial={handleClearInitial}
              onNavigate={handleNavigate}
            />
          ) : activeTab === 'inventory_manager' ? (
            <InventoryDashboardView orgId={selectedOrgId} language={language}
              initialItemId={selectedEntityId} onClearInitial={handleClearInitial}
              onNavigate={handleNavigate}
            />
          ) : activeTab === 'payrun' ? (
            <PayRunView  orgId={selectedOrgId} language={language}
              orgSettings={orgSettings} />
          ) : activeTab === 'employees' ? (
            <EmployeesView  orgId={selectedOrgId} language={language}
              orgSettings={orgSettings} />
          ) : activeTab === 'payrun_settings' ? (
            <PayrollSettingsView  orgId={selectedOrgId} language={language}
              orgSettings={orgSettings} />
          ) : activeTab === 'attendance' ? (
            <AttendanceView  orgId={selectedOrgId} language={language}
              theme={theme} />
          ) : activeTab === 'tasks_mops' ? (
            <TasksMOPSView  orgId={selectedOrgId} language={language}
              />
          ) : activeTab === 'pos_sell' ? (
            <POSView  orgId={selectedOrgId} language={language}
              selectedStoreId={selectedStoreId}
              setSelectedStoreId={setSelectedStoreId}
              resumedSaleId={resumedSaleId} 
              isReturn={isReturn}
              onClear={() => {
                setResumedSaleId(null);
                setIsReturn(false);
              }}
            />
          ) : activeTab === 'pos_history' ? (
            <POSSalesHistoryView  orgId={selectedOrgId} language={language}
              selectedStoreId={selectedStoreId}
              setSelectedStoreId={setSelectedStoreId}
              onContinueSale={(saleId) => {
                setResumedSaleId(saleId);
                setIsReturn(false);
                setActiveTab('pos_sell');
              }}
              onReturnSale={(saleId) => {
                setResumedSaleId(saleId);
                setIsReturn(true);
                setActiveTab('pos_sell');
              }}
            />
          ) : activeTab === 'pos_catalog' ? (
            <POSCatalogView  orgId={selectedOrgId} language={language}
              />
          ) : activeTab === 'purchase_orders' ? (
            <PurchaseOrdersView  orgId={selectedOrgId} language={language}
              initialPurchaseOrderId={selectedEntityId}
              onClearInitial={handleClearInitial}
              onMarkAsBilled={() => handleNavigate('bills')}
              onNavigate={handleNavigate}
            />
          ) : activeTab === 'bills' ? (
            <BillsView  orgId={selectedOrgId} orgSettings={orgSettings} language={language}
              initialBillId={selectedEntityId}
              onClearInitial={handleClearInitial}
              onNavigate={handleNavigate}
            />
          ) : activeTab === 'purchase_deliveries' ? (
            <PurchaseDeliveriesView orgId={selectedOrgId} language={language}
              initialDeliveryId={selectedEntityId}
              onClearInitial={handleClearInitial}
            />
          ) : activeTab === 'expense_submissions' ? (
            <ExpenseSubmissionsView  orgId={selectedOrgId} language={language}
              />
          ) : activeTab === 'landed_costs' ? (
            <LandedCostsView orgId={selectedOrgId} language={language} />
          ) : activeTab === 'customers' ? (
            <ContactsView 
              key="customers" 
              orgId={selectedOrgId} 
              orgSettings={orgSettings}
              language={language}
              initialType="Customer" 
              initialContactId={selectedEntityId}
              onClearInitial={handleClearInitial}
              onNavigate={handleNavigate} 
            />
          ) : activeTab === 'suppliers' ? (
            <ContactsView 
              key="suppliers" 
              orgId={selectedOrgId} 
              orgSettings={orgSettings}
              language={language}
              initialType="Supplier" 
              initialContactId={selectedEntityId}
              onClearInitial={handleClearInitial}
              onNavigate={handleNavigate} 
            />
          ) : activeTab === 'contacts' ? (
            <ContactsView 
              key="all-contacts" 
              orgId={selectedOrgId} 
              orgSettings={orgSettings}
              language={language}
              initialContactId={selectedEntityId}
              onClearInitial={handleClearInitial}
              onNavigate={handleNavigate} 
            />
          ) : activeTab === 'reports' ? (
            <ReportsView 
              orgId={selectedOrgId} 
              language={language}
              initialReportId={selectedEntityId}
              initialAccountId={selectedAccountId}
              initialContactId={selectedContactId}
              initialStartDate={selectedStartDate}
              initialEndDate={selectedEndDate}
              onClearInitial={handleClearInitial}
              orgSettings={orgSettings}
              onNavigate={(tab, id, extra) => {
                setActiveTab(tab);
                if (id) setSelectedEntityId(id);
                if (extra && extra.hasOwnProperty('accountId')) setSelectedAccountId(extra.accountId);
                if (extra && extra.hasOwnProperty('contactId')) setSelectedContactId(extra.contactId);
                if (extra && extra.hasOwnProperty('startDate')) setSelectedStartDate(extra.startDate);
                if (extra && extra.hasOwnProperty('endDate')) setSelectedEndDate(extra.endDate);
              }}
            />
          ) : activeTab === 'activity_log' ? (
            <ActivityLogView 
               orgId={selectedOrgId} 
               language={language} 
               initialTargetId={selectedEntityId}
            />
          ) : activeTab === 'accounting_dashboard' ? (
            <AccountingDashboardView 
              orgId={selectedOrgId} 
              settings={orgSettings}
              onNavigate={(tab, id) => {
                setActiveTab(tab);
                setSelectedEntityId(id || '');
              }}
            />
          ) : activeTab === 'accounting_configuration' ? (
            <AccountingConfigurationView 
              orgId={selectedOrgId} 
              language={language}
              onNavigate={(tab, id, extra) => {
                setActiveTab(tab as any);
                if (id) setSelectedEntityId(id);
                if (extra && extra.hasOwnProperty('accountId')) setSelectedAccountId(extra.accountId);
              }}
            />
          ) : activeTab === 'bank_accounts' ? (
            <BankAccountsView  orgId={selectedOrgId} language={language} initialAccountId={selectedEntityId}
                onClearInitial={() => setSelectedEntityId('')}
              />
          ) : activeTab === 'fixed_assets' ? (
            <FixedAssetsView  orgId={selectedOrgId} language={language}
              />
          ) : activeTab === 'manual_journal' ? (
            <ManualJournalsView  
              orgId={selectedOrgId} 
              orgSettings={orgSettings}
              language={language}
              initialAccountId={selectedAccountId}
            />
          ) : activeTab === 'customer_balances' ? (
            <CustomerBalancesView 
              orgId={selectedOrgId} 
              language={language}
              orgSettings={orgSettings}
              onNavigate={(tab, id, extra) => {
                setActiveTab(tab);
                if (id) setSelectedEntityId(id);
                if (extra && extra.hasOwnProperty('accountId')) setSelectedAccountId(extra.accountId);
                if (extra && extra.hasOwnProperty('contactId')) setSelectedContactId(extra.contactId);
              }}
            />
          ) : activeTab === 'vendor_balances' ? (
            <VendorBalancesView 
              orgId={selectedOrgId} 
              language={language}
              orgSettings={orgSettings}
              onNavigate={(tab, id, extra) => {
                setActiveTab(tab);
                if (id) setSelectedEntityId(id);
                if (extra && extra.hasOwnProperty('accountId')) setSelectedAccountId(extra.accountId);
                if (extra && extra.hasOwnProperty('contactId')) setSelectedContactId(extra.contactId);
              }}
            />
          ) : activeTab === 'trial_balance' ? (
            <TrialBalanceView 
              orgId={selectedOrgId} 
              language={language}
              onNavigate={(tab, id, extra) => {
                setActiveTab(tab);
                if (id) setSelectedEntityId(id);
                if (extra && extra.hasOwnProperty('accountId')) setSelectedAccountId(extra.accountId);
                if (extra && extra.hasOwnProperty('contactId')) setSelectedContactId(extra.contactId);
                if (extra && extra.hasOwnProperty('startDate')) setSelectedStartDate(extra.startDate);
                if (extra && extra.hasOwnProperty('endDate')) setSelectedEndDate(extra.endDate);
              }}
            />
          ) : activeTab === 'discussions' ? (
            <DiscussionsView  
              orgId={selectedOrgId} 
              language={language}
              initialThreadId={selectedEntityId}
              onClearInitial={handleClearInitial}
            />
          ) : activeTab === 'settings' ? (
            <SettingsView  orgId={selectedOrgId} language={language}
              />
          ) : activeTab === 'stores' ? (
            <StoresView  orgId={selectedOrgId} language={language}
              />
          ) : activeTab === 'help' ? (
            <HelpView orgId={selectedOrgId} language={language} />
          ) : activeTab === 'super_admin' && hasPermission('super_admin') ? (
            <SuperAdminView />
          ) : activeTab === 'profile' ? (
            <ProfileView 
              orgId={selectedOrgId} 
              language={language}
              onLayoutPreferenceChange={(pref) => setLayoutPreference(pref as any)}
              onLanguageChange={(lang) => setLanguage(normalizeLanguage(lang))}
              isSuperAdmin={isSuperAdmin}
              onNavigate={(tab) => handleNavigate(tab)}
            />
          ) : activeTab === 'dashboard' ? (
            <div className="animate-in fade-in zoom-in duration-500">
                <ExecutiveDashboard orgId={selectedOrgId} orgSettings={orgSettings} onNavigate={setActiveTab} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 opacity-50 italic serif">
              <Loader2 className="w-12 h-12 animate-spin mb-4" />
              <p className="text-xl">{t.coming_soon}</p>
            </div>
          )}
          
        </div>
      </main>

          {user && selectedOrgId && !showAIChat && (
            <button
              type="button"
              onClick={handleAiLauncherClick}
              onPointerDown={handleAiLauncherPointerDown}
              onPointerMove={handleAiLauncherPointerMove}
              onPointerUp={handleAiLauncherPointerUp}
              onPointerCancel={handleAiLauncherPointerUp}
              style={{ left: aiLauncherPosition.x, top: aiLauncherPosition.y, touchAction: 'none' }}
              className="fixed z-[9998] flex h-14 w-14 cursor-grab items-center justify-center rounded-full bg-sky-600 text-white shadow-xl shadow-sky-900/20 transition-[box-shadow,transform] hover:scale-105 hover:bg-sky-700 focus:outline-none focus:ring-4 focus:ring-sky-500/30 active:cursor-grabbing active:scale-95 print:hidden"
              aria-label={language === 'ar' ? 'فتح مساعد المؤسسة الذكي' : 'Open organization AI'}
              title={language === 'ar' ? 'اسحب للتحريك أو انقر لفتح مساعد المؤسسة الذكي' : 'Drag to move or click to open organization AI'}
            >
              <MessageSquare className="h-6 w-6" />
            </button>
          )}
          {user && selectedOrgId && (
            <AIChatBot
              orgId={selectedOrgId}
              orgName={organizations.find(organization => organization.id === selectedOrgId)?.name}
              isOpen={showAIChat}
              onClose={() => setShowAIChat(false)}
            />
          )}
        </div>
    </LicenseGate>
  );
}

function NavItem({ icon, label, active, onClick, collapsed, theme }: { icon: React.ReactNode, label: string, active: boolean, onClick: () => void, collapsed: boolean, theme: any }) {
  return (
    <motion.button 
      onClick={onClick}
      whileHover={{ x: collapsed ? 0 : 3 }}
      whileTap={{ scale: 0.97 }}
      className={cn(
        "w-full flex items-center gap-4 p-3 transition-all duration-200 relative",
        theme.rounded,
        active 
          ? cn(theme.accent, theme.accentText, "shadow-sm") 
          : "hover:bg-primary/5",
        collapsed && "justify-center px-0"
      )}
    >
      {active && !collapsed && (
        <motion.span 
          layoutId="nav-active"
          className="nav-active-indicator"
          transition={{ type: "spring", stiffness: 500, damping: 35 }}
        />
      )}
      <motion.span
        animate={{ scale: active ? 1.1 : 1 }}
        transition={{ type: "spring", stiffness: 400, damping: 25 }}
      >
        {icon}
      </motion.span>
      {!collapsed && (
        <motion.span
          initial={{ opacity: 0, x: -4 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -4 }}
          className={cn("text-sm font-medium", active && "font-bold")}
        >
          {label}
        </motion.span>
      )}
      {active && collapsed && (
        <motion.div
          layoutId="nav-collapsed-dot"
          className="absolute right-1.5 w-1.5 h-1.5 rounded-full bg-current"
          transition={{ type: "spring", stiffness: 500, damping: 35 }}
        />
      )}
    </motion.button>
  );
}

function NavGroup({ icon, label, isExpanded, onToggle, children, collapsed, theme }: { icon: React.ReactNode, label: string, isExpanded: boolean, onToggle: () => void, children: React.ReactNode, collapsed: boolean, theme: any }) {
  return (
    <div className="space-y-1">
      <motion.button 
        onClick={onToggle}
        whileHover={{ x: collapsed ? 0 : 2 }}
        whileTap={{ scale: 0.98 }}
        className={cn(
          "w-full flex items-center justify-between p-3 transition-all duration-200 hover:bg-primary/5",
          theme.rounded,
          collapsed && "justify-center px-0"
        )}
      >
        <div className="flex items-center gap-4">
          {icon}
          {!collapsed && <span className="text-sm font-medium">{label}</span>}
        </div>
        {!collapsed && (
          <motion.span
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown className="w-4 h-4 opacity-40" />
          </motion.span>
        )}
      </motion.button>
      <AnimatePresence initial={false}>
        {isExpanded && !collapsed && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className={cn("ms-9 space-y-1 border-s ps-2 overflow-hidden", (theme?.border ? String(theme.border).replace('border-', 'border-opacity-10 border-') : undefined) || '')}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SubNavItem({ label, active, onClick, theme }: { label: string, active: boolean, onClick: () => void, theme: any }) {
  return (
    <button 
      onClick={onClick}
      className={cn(
        "w-full text-start p-2 text-xs transition-all",
        theme.rounded,
        active ? cn(theme.accent, theme.accentText, "font-bold") : "hover:bg-primary/5 opacity-70"
      )}
    >
      {label}
    </button>
  );
}

function StatCard({ title, value, change, trend, icon, theme }: { title: string, value: string, change: string, trend: 'up' | 'down', icon: React.ReactNode, theme: any }) {
  return (
    <div className={cn("p-6 border", theme.card, theme.rounded, theme.border)}>
      <div className="flex items-center justify-between mb-4">
        <div className={cn("p-2 rounded-lg", theme.accent, theme.accentText)}>
          {icon}
        </div>
        <span className={cn(
          "text-xs font-bold px-2 py-1 rounded-full",
          trend === 'up' ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
        )}>
          {change}
        </span>
      </div>
      <p className="text-xs opacity-50 uppercase tracking-wider font-bold">{title}</p>
      <p className="text-2xl font-bold mt-1 font-mono tracking-tighter">{value}</p>
    </div>
  );
}
